'use client';

import classNames from 'classnames';
import { useMemo } from 'react';
import { FeCarouselV2 } from '../../components-internal/FeCarousel';
import { toJsonLd } from '../../components-internal/toJsonLd';
import {
  ComponentCustomerReviewsBlock,
  ComponentCustomerReviewsBlockReview,
  EnumCustomerReviewsBlockAlign,
} from '$/(cms)/_modules/cmsService/types';
import { CustomerReviewBlockStars } from '$/components/cms/components-user/CustomerReviewsBlock/components/CustomerReviewBlockStars';
import {
  clampRating,
  getBackgroundClass,
  getSourceLabel,
  getSourceLabelUrl,
  transformSeoData,
} from '$/components/cms/components-user/CustomerReviewsBlock/utils';
import { useClientContext } from '$/modules/context/client';

export const CustomerReviewsBlock = (
  props: ComponentCustomerReviewsBlock,
): JSX.Element | null => {
  const { brand, host } = useClientContext();

  const reviews: ComponentCustomerReviewsBlockReview[] = useMemo(
    () =>
      props.reviewsSelection?.filter(
        (review): review is ComponentCustomerReviewsBlockReview => {
          if (!review) {
            return false;
          }

          return review.visible !== false;
        },
      ) ?? [],
    [props.reviewsSelection],
  );

  const isCenterAligned =
    props.align === EnumCustomerReviewsBlockAlign.Center || !props.align;

  const sourceLabel = getSourceLabel(props.reviewsSource);
  const sourceLabelUrl = getSourceLabelUrl(brand, props.reviewsSource);

  const ratingValues = reviews
    .map((review) => clampRating(review.rating))
    .filter((rating): rating is number => typeof rating === 'number');

  const aggregateRatingValue = ratingValues.length
    ? Number(
        (
          ratingValues.reduce((sum, value) => sum + value, 0) /
          ratingValues.length
        ).toFixed(1),
      )
    : undefined;

  const structuredData = useMemo(
    () =>
      transformSeoData(
        brand,
        host,
        ratingValues.length,
        reviews,
        sourceLabel,
        aggregateRatingValue,
      ),
    [
      aggregateRatingValue,
      brand,
      host,
      ratingValues.length,
      reviews,
      sourceLabel,
    ],
  );

  const shouldAutoplay = reviews.length > 1;

  return (
    <section
      data-component="customer-reviews-block"
      className={classNames(
        'px-spacing/padding/section-x py-spacing/padding/section-y',
        getBackgroundClass(props.bgColorVariant),
      )}
    >
      <div className="mx-auto flex w-full flex-col gap-spacing/gap/y md:w-general/narrow-content-max-width-3 md:max-w-general/narrow-content-max-width-3">
        <div
          className={classNames(
            'mx-auto flex w-full flex-col gap-spacing/gap/soft-8px',
            {
              'md:w-general/narrow-content-max-width-1 md:max-w-general/narrow-content-max-width-1':
                isCenterAligned,
            },
          )}
        >
          {props.blockTitle && (
            <div
              className={classNames(
                'mhg-alpha-typography-display-1 text-text/primary',
                {
                  'text-center': isCenterAligned,
                  'text-left': !isCenterAligned,
                },
              )}
            >
              {props.blockTitle}
            </div>
          )}

          {props.blockBody && (
            <div
              className={classNames(
                'mhg-alpha-typography-body-1 text-text/secondary',
                {
                  'text-center': isCenterAligned,
                  'text-left': !isCenterAligned,
                },
              )}
            >
              {props.blockBody}
            </div>
          )}
        </div>
        {reviews.length > 0 && (
          <div className="-mr-spacing/gap/x-sm">
            <FeCarouselV2
              slidesPerView={1}
              slidesPerViewBreakpoints={[
                [Math.min(2, reviews.length), 768],
                [Math.min(3, reviews.length), 1280],
              ]}
              autoplay={
                shouldAutoplay
                  ? {
                      delay: 5000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }
                  : false
              }
              pagination={{ showOnMobile: true, showOnDesktop: true }}
              slideClassName="!h-auto !mr-0"
            >
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="flex size-full flex-col pr-spacing/gap/x-sm"
                >
                  <article className="flex h-full flex-col gap-spacing/gap/soft-12px rounded-radius/card border border-borders/default px-spacing/padding/x-sm py-spacing/padding/y bg-fill/card">
                    <div className="flex items-center gap-spacing/gap/soft-12px">
                      <CustomerReviewBlockStars
                        rating={clampRating(review.rating)}
                      />
                    </div>

                    {review.reviewTitle && (
                      <h3 className="mhg-alpha-typography-subheading-2 text-text/primary">
                        {review.reviewTitle}
                      </h3>
                    )}

                    {review.reviewBody && (
                      <div className="mhg-alpha-typography-body-2-relaxed line-clamp-5 text-text/secondary">
                        {review.reviewBody}
                      </div>
                    )}

                    <div className="mt-auto flex items-center gap-gap/x-sm">
                      <div className="flex size-provider/asset-cell-asset-card-height shrink-0 items-center justify-center overflow-hidden rounded-full border border-borders/default bg-fill/base text-text/primary">
                        {review.avatarUrl ? (
                          <img
                            src={review.avatarUrl}
                            alt={review.reviewerName ?? 'Reviewer avatar'}
                            loading="lazy"
                            className="size-full object-cover"
                          />
                        ) : (
                          <span className="mhg-alpha-typography-bullet-1">
                            {review.reviewerName?.charAt(0) ?? 'R'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col">
                        {review.reviewerName && (
                          <span className="mhg-alpha-typography-bullet-2 text-text/primary">
                            {review.reviewerName}
                          </span>
                        )}
                        <span className="mhg-alpha-referral-history-wrapper-content-accordion-caption text-text/secondary">
                          Reviewed on{' '}
                          <a
                            href={sourceLabelUrl}
                            target="_blank"
                            className="underline text-text/link"
                            rel="noreferrer"
                          >
                            {sourceLabel}
                          </a>
                        </span>
                      </div>
                    </div>
                  </article>
                </div>
              ))}
            </FeCarouselV2>
          </div>
        )}
      </div>

      {structuredData && (
        <script
          type="application/ld+json"
          suppressHydrationWarning
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: toJsonLd(structuredData) }}
        />
      )}
    </section>
  );
};
